"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { IHeroImage, IHeroPill } from "@/sanity/pages/PageHomeTemplate";
import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { FadeIn } from "@/components/global/utils/FadeIn";

interface HeroSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: PortableTextBlock[];
  // `pills` is still provided by the page but no longer rendered in this layout.
  pills?: IHeroPill[];
  cta?: GROQButton;
  images?: IHeroImage[];
  /** Video-only background slides; each uses its thumbnail as the poster. */
  videos?: IMuxVideoPlayer[];
}

// Carbon "Volume--mute" glyph (Figma node 1845:1845), 22×22 viewBox.
const VolumeMuteIcon = () => (
  <svg
    viewBox="0 0 22 22"
    fill="none"
    className="w-full h-full"
    aria-hidden
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21.3125 8.53183L20.3431 7.56245L17.875 10.0306L15.4069 7.56245L14.4375 8.53183L16.9056 11L14.4375 13.4681L15.4069 14.4375L17.875 11.9693L20.3431 14.4375L21.3125 13.4681L18.8444 11L21.3125 8.53183Z"
      fill="currentColor"
    />
    <path
      d="M12.375 20.625C12.284 20.6246 12.1941 20.6062 12.1103 20.5707C12.0265 20.5353 11.9505 20.4837 11.8869 20.4187L6.64813 15.125H2.0625C1.88016 15.125 1.7053 15.0525 1.57636 14.9236C1.44743 14.7947 1.375 14.6198 1.375 14.4375V7.56245C1.375 7.38012 1.44743 7.20525 1.57636 7.07632C1.7053 6.94739 1.88016 6.87495 2.0625 6.87495H6.64813L11.8869 1.5812C12.0157 1.45315 12.1899 1.38128 12.3716 1.38128C12.5532 1.38128 12.7274 1.45315 12.8563 1.5812C12.9858 1.70814 13.0599 1.88111 13.0625 2.06245V19.9375C13.0625 20.1198 12.9901 20.2947 12.8611 20.4236C12.7322 20.5525 12.5573 20.625 12.375 20.625ZM2.75 13.75H6.875C7.07541 13.7486 7.2691 13.8222 7.41812 13.9562L11.6875 18.2668V3.73308L7.41812 8.0437C7.2691 8.17771 7.07541 8.25127 6.875 8.24995H2.75V13.75Z"
      fill="currentColor"
    />
  </svg>
);

// Matching "Volume--up": the same speaker body plus two output arcs.
const VolumeUpIcon = () => (
  <svg
    viewBox="0 0 22 22"
    fill="none"
    className="w-full h-full"
    aria-hidden
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.375 20.625C12.284 20.6246 12.1941 20.6062 12.1103 20.5707C12.0265 20.5353 11.9505 20.4837 11.8869 20.4187L6.64813 15.125H2.0625C1.88016 15.125 1.7053 15.0525 1.57636 14.9236C1.44743 14.7947 1.375 14.6198 1.375 14.4375V7.56245C1.375 7.38012 1.44743 7.20525 1.57636 7.07632C1.7053 6.94739 1.88016 6.87495 2.0625 6.87495H6.64813L11.8869 1.5812C12.0157 1.45315 12.1899 1.38128 12.3716 1.38128C12.5532 1.38128 12.7274 1.45315 12.8563 1.5812C12.9858 1.70814 13.0599 1.88111 13.0625 2.06245V19.9375C13.0625 20.1198 12.9901 20.2947 12.8611 20.4236C12.7322 20.5525 12.5573 20.625 12.375 20.625ZM2.75 13.75H6.875C7.07541 13.7486 7.2691 13.8222 7.41812 13.9562L11.6875 18.2668V3.73308L7.41812 8.0437C7.2691 8.17771 7.07541 8.25127 6.875 8.24995H2.75V13.75Z"
      fill="currentColor"
    />
    <path
      d="M15.8125 8.25C16.9375 9.375 16.9375 12.625 15.8125 13.75"
      stroke="currentColor"
      strokeWidth="1.375"
      strokeLinecap="round"
    />
    <path
      d="M18.0625 6C20.3125 8.25 20.3125 13.75 18.0625 16"
      stroke="currentColor"
      strokeWidth="1.375"
      strokeLinecap="round"
    />
  </svg>
);

const SLIDE_INTERVAL = 25000;

// Full-bleed background carousel: each slide's poster (and video, when present)
// fills the whole section. Slides cross-fade; only the active slide mounts its
// video so we never buffer more than one stream at a time (Figma 1845:1803).
function HeroBackground({
  images,
  active,
  muted,
  canLoop,
  onProgress,
  onEnded,
}: {
  images: IHeroImage[];
  active: number;
  muted: boolean;
  canLoop: boolean;
  onProgress?: (progress: number) => void;
  onEnded?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // React only writes `muted` as the initial attribute, so drive it imperatively
  // on the active video whenever the mute state or active slide changes.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, active]);

  return (
    <div className="overflow-hidden absolute inset-0 bg-black">
      {images.map((slide, i) => {
        const isActive = i === active;
        const hasVideo = Boolean(slide?.video?.muxVideo?.playbackId);
        return (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
              isActive ? "opacity-100" : "opacity-0"
            }`}>
            {slide?.image && (
              <Image
                src={slide.image}
                alt={slide.name ?? ""}
                fill
                priority={i === 0}
                quality={100}
                className="object-cover"
              />
            )}
            {hasVideo && isActive && (
              <HLSVideoPlayer
                ref={videoRef}
                source={slide.video!}
                autoPlay
                muted={muted}
                // When there are multiple slides we rely on `ended` to advance,
                // so the video must not loop. A single slide loops forever.
                loop={!canLoop}
                playsInline
                poster={slide.image?.src}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  if (v.duration > 0) onProgress?.(v.currentTime / v.duration);
                }}
                onEnded={onEnded}
                className="object-cover absolute inset-0 w-full h-full"
              />
            )}
          </div>
        );
      })}

      {/* Legibility scrim — darkens the bottom (and a touch of the top) so the
          copy and navbar stay readable over bright footage. */}
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.00)_0.4%,_#000_88.86%),_linear-gradient(0deg,_rgba(0,_0,_0,_0.20)_0%,_rgba(0,_0,_0,_0.20)_100%)] z-[2]" />
    </div>
  );
}

// Chip + title + subtitle + CTA. Reused by both the desktop overlay and the
// mobile stack; only its wrapper (position, gap, width) changes per layout.
function HeroCopy({
  chip,
  title,
  subtitle,
  cta,
  ctaFullWidth,
}: {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: PortableTextBlock[];
  cta?: GROQButton;
  ctaFullWidth?: boolean;
}) {
  return (
    <>
      <div className="flex flex-col w-full gap-[3rem]">
        {chip && (
          <FadeIn
            delay={0}
            className="hidden lg:block">
            <p className="m-0 uppercase font-spacemono text-[1.75rem] text-[#b8d2e2]">
              <span className="text-[#b8d2e2]/20">|</span>
              <span className="text-[#b8d2e2]/40">|</span>
              <span className="text-[#b8d2e2]/60">|</span>
              <span> {chip}</span>
            </p>
          </FadeIn>
        )}
        <div className="flex flex-col w-full lg:gap-0 gap-[1rem]">
          {title && (
            <FadeIn
              delay={0.1}
              className="richtext richtext-start prose-em:font-news prose-em:italic prose-headings:font-news prose-headings:leading-[1.1] prose-headings:tracking-[-0.11rem] prose-headings:text-white prose-headings:m-0 prose-headings:text-[5.5rem] lg:prose-headings:tracking-[-0.15rem] lg:prose-headings:text-[7.5rem]">
              <PortableText
                value={title}
                components={richTextComponent}
              />
            </FadeIn>
          )}
          {subtitle && (
            <FadeIn
              delay={0.2}
              className="richtext richtext-start prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.4] prose-p:tracking-[0.045rem] prose-p:text-white/60 lg:prose-p:leading-[1.5] lg:prose-p:text-white/70">
              <PortableText
                value={subtitle}
                components={richTextComponent}
              />
            </FadeIn>
          )}
        </div>
      </div>

      {cta && (
        <FadeIn
          delay={0.3}
          className={ctaFullWidth ? "w-full" : ""}>
          <CTAButton
            data={cta}
            fullWidth={ctaFullWidth}
          />
        </FadeIn>
      )}
    </>
  );
}

// Carousel dot indicators. `className` positions the row per layout. The active
// dot is wide with a dim background; a solid white inner bar fills it in sync
// with the current video's playback progress (0 → 1).
function HeroDots({
  slides,
  active,
  onSelect,
  progress,
  className,
}: {
  slides: IHeroImage[];
  active: number;
  onSelect: (i: number) => void;
  progress: number;
  className?: string;
}) {
  return (
    <div className={`flex items-center h-[1rem] gap-[1.5rem] ${className ?? ""}`}>
      {slides.map((_, i) => {
        const isActive = i === active;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`relative overflow-hidden h-full rounded-[0.25rem] transition-all duration-300 ${
              isActive ? "w-[4rem] bg-white/30" : "w-[1rem] bg-white opacity-40"
            }`}>
            {isActive && (
              <span
                aria-hidden
                className="block absolute inset-y-0 left-0 bg-white rounded-[0.25rem]"
                style={{
                  width: `${Math.max(0, Math.min(progress * 100, 100))}%`,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// Glass mute toggle (Figma 1845:1844 / 1914:535). `className` positions it.
function MuteButton({ muted, onToggle, className }: { muted: boolean; onToggle: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={muted ? "Unmute video" : "Mute video"}
      aria-pressed={!muted}
      className={`flex justify-center items-center text-white rounded-full border border-white/[0.14] bg-white/[0.02] backdrop-blur-[1.75rem] p-[2rem] lg:p-[2.25rem] ${className ?? ""}`}>
      <span className="block w-[2.25rem] h-[2.25rem] lg:w-[2.75rem] lg:h-[2.75rem]">
        {muted ? <VolumeMuteIcon /> : <VolumeUpIcon />}
      </span>
    </button>
  );
}

export default function HeroSection({ chip, title, subtitle, cta, images, videos }: HeroSectionProps) {
  // Image slides + video-only slides (poster = each video's thumbnail) share one
  // carousel; the background already plays a slide's `video` when present.
  const slides = useMemo<IHeroImage[]>(
    () => [
      // ...(images ?? []),
      ...(videos ?? [])
        .filter((v) => v?.muxVideo?.playbackId || v?.embed)
        .map((v) => ({
          image: (v?.thumbnail as GROQImage) ?? undefined,
          video: v,
        })),
    ],
    [images, videos],
  );
  const count = slides.length;
  const canLoop = count > 1;

  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);

  const goToSlide = useCallback((i: number) => {
    setProgress(0);
    setActive(i);
  }, []);

  const advance = useCallback(() => {
    if (!canLoop) return;
    setProgress(0);
    setActive((a) => (a + 1) % count);
  }, [canLoop, count]);

  // Touch swipe on the video (mobile): left → next slide, right → previous.
  // Only fires on a dominantly-horizontal gesture so vertical page scrolling
  // over the video keeps working.
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = touchStart.current;
      touchStart.current = null;
      if (!start || !canLoop) return;
      const dx = e.changedTouches[0].clientX - start.x;
      const dy = e.changedTouches[0].clientY - start.y;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
      setProgress(0);
      setActive((a) => (dx < 0 ? (a + 1) % count : (a - 1 + count) % count));
    },
    [canLoop, count],
  );

  // Fallback timer: only kicks in when the current slide has no playable video
  // (so `onEnded` will never fire). Video slides drive their own timing.
  useEffect(() => {
    if (!canLoop) return;
    const current = slides[active];
    if (current?.video?.muxVideo?.playbackId || current?.video?.embed) return;
    const id = setInterval(advance, SLIDE_INTERVAL);
    return () => clearInterval(id);
  }, [active, advance, canLoop, slides]);

  return (
    <section className="flex relative flex-col bg-black lg:block lg:overflow-hidden lg:h-[96svh] lg:min-h-[64rem]">
      {/* Video — a 16:9 block at the top on mobile, a full-bleed background on desktop. */}
      {count > 0 && (
        <div
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="overflow-hidden relative w-full lg:absolute lg:inset-0 lg:h-full touch-pan-y aspect-video lg:aspect-auto">
          <HeroBackground
            images={slides}
            active={active}
            muted={muted}
            canLoop={canLoop}
            onProgress={setProgress}
            onEnded={advance}
          />
        </div>
      )}

      {/* MOBILE stack — dots + mute row, then copy, then full-width CTA. */}
      <div className="flex relative z-10 flex-col lg:hidden">
        {count > 0 && (
          <div className="flex justify-between items-center w-full px-[2rem] mt-[2.5rem]">
            {canLoop ? (
              <HeroDots
                slides={slides}
                active={active}
                onSelect={goToSlide}
                progress={progress}
              />
            ) : (
              <span />
            )}
            <MuteButton
              muted={muted}
              onToggle={() => setMuted((m) => !m)}
            />
          </div>
        )}

        <div className="flex flex-col items-start w-full px-[2rem] pb-[4rem] mt-[2rem] gap-[2.25rem] lg:mt-[3rem]">
          <HeroCopy
            chip={chip}
            title={title}
            subtitle={subtitle}
            cta={cta}
            ctaFullWidth
          />
        </div>
      </div>

      {/* DESKTOP overlay — aligned to the same 150rem container the page uses. */}
      <div className="hidden absolute inset-0 z-10 lg:block">
        <div className="relative mx-auto h-full w-[min(150rem,100%)]">
          {/* Copy — bottom-left */}
          <div className="flex absolute left-0 flex-col items-start bottom-[13rem] gap-[3.75rem] w-[min(67.625rem,100%)]">
            <HeroCopy
              chip={chip}
              title={title}
              subtitle={subtitle}
              cta={cta}
            />
          </div>

          {/* Dot indicators — bottom center */}
          {canLoop && (
            <HeroDots
              slides={slides}
              active={active}
              onSelect={goToSlide}
              progress={progress}
              className="absolute left-1/2 z-20 -translate-x-1/2 bottom-[11rem]"
            />
          )}

          {/* Mute toggle — bottom right */}
          {count > 0 && (
            <MuteButton
              muted={muted}
              onToggle={() => setMuted((m) => !m)}
              className="absolute right-0 z-20 bottom-[10rem]"
            />
          )}
        </div>
      </div>
    </section>
  );
}
